// Geography — content sections & topics
// Master index for geographycontent1.js to geographycontent12.js
//
// This file is deliberately defensive because the Geography content files may use
// slightly different shapes while the subject is being built.
// It supports both:
//
// 1) Flat practice arrays:
//    practice: [
//      { type: 'mcq', question: '...', options: [...], correct: 1 }
//    ]
//
// 2) Tiered practice banks:
//    practice: {
//      warmUp: [],
//      level1: [],
//      level2: [],
//      level3: [],
//      examStyle: [],
//      challenge: []
//    }

import * as geo1 from '../../geographycontent1';
import * as geo2 from '../../geographycontent2';
import * as geo3 from '../../geographycontent3';
import * as geo4 from '../../geographycontent4';
import * as geo5 from '../../geographycontent5';
import * as geo6 from '../../geographycontent6';
import * as geo7 from '../../geographycontent7';
import * as geo8 from '../../geographycontent8';
import * as geo9 from '../../geographycontent9';
import * as geo10 from '../../geographycontent10';
import * as geo11 from '../../geographycontent11';
import * as geo12 from '../../geographycontent12';

const SECTION_META = [
  {
    id: 1,
    slug: 'locational-knowledge',
    title: 'Locational Knowledge',
    color: 'emerald',
    icon: 'Globe',
    description:
      'Continents, oceans, biomes, Africa, India, China, Russia and the Middle East.',
    aqaPaper: 'Paper 3',
  },
  {
    id: 2,
    slug: 'tectonic-activity',
    title: 'Tectonic Activity',
    color: 'rose',
    icon: 'Mountains',
    description:
      'Plate tectonics, earthquakes, volcanoes and surviving tectonic hazards.',
    aqaPaper: 'Paper 1',
  },
  {
    id: 3,
    slug: 'rocks-weathering-soil',
    title: 'Rocks, Weathering and Soil',
    color: 'amber',
    icon: 'Stack',
    description:
      'Rock types, weathering processes and how soils form.',
    aqaPaper: 'Paper 1',
  },
  {
    id: 4,
    slug: 'weather-and-climate',
    title: 'Weather and Climate',
    color: 'sky',
    icon: 'CloudRain',
    description:
      'Climate change, world climate zones, microclimates, weather forecasting and the water cycle.',
    aqaPaper: 'Paper 1',
  },
  {
    id: 5,
    slug: 'hydrology-coasts-glaciation',
    title: 'Hydrology, Coasts and Glaciation',
    color: 'blue',
    icon: 'Waves',
    description:
      'Drainage basins, rivers, flooding, coasts and glacial landscapes.',
    aqaPaper: 'Paper 1',
  },
  {
    id: 6,
    slug: 'population-and-urbanisation',
    title: 'Population and Urbanisation',
    color: 'violet',
    icon: 'UsersThree',
    description:
      'Population distribution, growth, migration and how cities expand.',
    aqaPaper: 'Paper 2',
  },
  {
    id: 7,
    slug: 'development',
    title: 'Development',
    color: 'emerald',
    icon: 'TrendUp',
    description:
      'Measuring development, the development gap, world trade and aid.',
    aqaPaper: 'Paper 2',
  },
  {
    id: 8,
    slug: 'economic-activity-resources',
    title: 'Economic Activity and Natural Resources',
    color: 'orange',
    icon: 'Factory',
    description:
      'Industry classification, energy, water, food and the management of natural resources.',
    aqaPaper: 'Paper 2',
  },
  {
    id: 9,
    slug: 'influencing-the-environment',
    title: 'Influencing the Environment',
    color: 'lime',
    icon: 'Leaf',
    description:
      'Pollution, acid rain, global warming, soil erosion, conservation and environmental management.',
    aqaPaper: 'Paper 1',
  },
  {
    id: 10,
    slug: 'contrasting-regions',
    title: 'Case Studies — Nigeria and Japan',
    color: 'pink',
    icon: 'MapPin',
    description:
      'Two contrasting regions compared: Nigeria as a newly emerging economy and Japan as a high-income country.',
    aqaPaper: 'Paper 2',
  },
  {
    id: 11,
    slug: 'enquiry-and-skills',
    title: 'Geographical Enquiry and Skills',
    color: 'teal',
    icon: 'MagnifyingGlass',
    description:
      'Fieldwork, sampling, OS maps, graphs, map interpretation and geographical enquiry.',
    aqaPaper: 'Paper 3',
  },
  {
    id: 12,
    slug: 'exam-practice',
    title: 'Exam Practice',
    color: 'indigo',
    icon: 'GraduationCap',
    description:
      'AQA Papers 1, 2 and 3 — structure, timing, command words and exam technique.',
    aqaPaper: 'All',
  },
];

const sectionModules = [
  geo1,
  geo2,
  geo3,
  geo4,
  geo5,
  geo6,
  geo7,
  geo8,
  geo9,
  geo10,
  geo11,
  geo12,
];

export const GEOGRAPHY_PRACTICE_TIERS = [
  'warmUp',
  'level1',
  'level2',
  'level3',
  'examStyle',
  'challenge',
];

function getPossibleSection(module, sectionNumber) {
  if (!module) return null;

  return (
    module.default ||
    module[`geographyContent${sectionNumber}`] ||
    module[`geographycontent${sectionNumber}`] ||
    module[`geographySection${sectionNumber}`] ||
    module[`geographysection${sectionNumber}`] ||
    module[`section${sectionNumber}`] ||
    null
  );
}

function extractTopics(module, sectionNumber) {
  if (!module) return [];

  if (Array.isArray(module.default)) return module.default;

  const namedTopics =
    module[`section${sectionNumber}Topics`] ||
    module[`geographyContent${sectionNumber}Topics`] ||
    module[`geographycontent${sectionNumber}Topics`] ||
    module[`geographyTopics${sectionNumber}`];

  if (Array.isArray(namedTopics)) return namedTopics;

  const possibleSection = getPossibleSection(module, sectionNumber);

  if (Array.isArray(possibleSection)) return possibleSection;

  if (possibleSection?.topics && Array.isArray(possibleSection.topics)) {
    return possibleSection.topics;
  }

  return [];
}

function normalisePracticeToTieredBank(practice) {
  if (!practice) {
    return {
      warmUp: [],
      level1: [],
      level2: [],
      level3: [],
      examStyle: [],
      challenge: [],
    };
  }

  // Existing geographycontent files may use:
  // practice: [...]
  // In that case, expose the same questions through warmUp and level1 so
  // the topic page does not show an empty tier.
  if (Array.isArray(practice)) {
    return {
      warmUp: practice,
      level1: practice,
      level2: [],
      level3: [],
      examStyle: [],
      challenge: [],
    };
  }

  // Tiered structure.
  if (typeof practice === 'object') {
    return {
      warmUp: Array.isArray(practice.warmUp) ? practice.warmUp : [],
      level1: Array.isArray(practice.level1) ? practice.level1 : [],
      level2: Array.isArray(practice.level2) ? practice.level2 : [],
      level3: Array.isArray(practice.level3) ? practice.level3 : [],
      examStyle: Array.isArray(practice.examStyle) ? practice.examStyle : [],
      challenge: Array.isArray(practice.challenge) ? practice.challenge : [],
    };
  }

  return {
    warmUp: [],
    level1: [],
    level2: [],
    level3: [],
    examStyle: [],
    challenge: [],
  };
}

export function getGeographyPracticeBank(topic, tier = 'warmUp') {
  const bank = normalisePracticeToTieredBank(topic?.practice);

  if (bank[tier]?.length) return bank[tier];

  // Sensible fallback order so a page does not appear empty simply because
  // a specific tier has not been authored yet.
  return (
    bank.warmUp.length ? bank.warmUp :
    bank.level1.length ? bank.level1 :
    bank.level2.length ? bank.level2 :
    bank.level3.length ? bank.level3 :
    bank.examStyle.length ? bank.examStyle :
    bank.challenge.length ? bank.challenge :
    []
  );
}

export function getGeographyPracticeCount(topic) {
  if (!topic) return 0;

  const practice = topic.practice;

  if (Array.isArray(practice)) {
    return practice.length;
  }

  if (practice && typeof practice === 'object') {
    const bank = normalisePracticeToTieredBank(practice);

    return GEOGRAPHY_PRACTICE_TIERS.reduce(
      (total, tier) => total + (bank[tier]?.length || 0),
      0
    );
  }

  return 0;
}

function normaliseQuestion(question, index = 0, topicId = 'geography-topic') {
  if (!question || typeof question !== 'object') return question;

  const id = question.id || `${topicId}-q-${index + 1}`;
  const type = question.type || 'mcq';

  // MCQ geography files commonly use:
  // correct: 1
  // where 1 is the correct option index.
  if (type === 'mcq' || type === 'multiple-choice') {
    const correctIndex =
      typeof question.correct === 'number'
        ? question.correct
        : typeof question.correctIndex === 'number'
          ? question.correctIndex
          : typeof question.answerIndex === 'number'
            ? question.answerIndex
            : null;

    const correctAnswer =
      question.correctAnswer ||
      question.answer ||
      question.expected_answer ||
      (correctIndex !== null ? question.options?.[correctIndex] : undefined);

    return {
      ...question,
      id,
      type: 'mcq',
      correctIndex,
      correctAnswer,
      answer: question.answer || correctAnswer,
    };
  }

  // True/false geography files commonly use:
  // correct: true
  if (type === 'true-false' || type === 'truefalse' || type === 'boolean') {
    const booleanAnswer =
      typeof question.correct === 'boolean'
        ? question.correct
        : typeof question.answer === 'boolean'
          ? question.answer
          : typeof question.correctAnswer === 'boolean'
            ? question.correctAnswer
            : null;

    return {
      ...question,
      id,
      type: 'true-false',
      correct: booleanAnswer,
      correctAnswer:
        booleanAnswer === null ? question.correctAnswer : booleanAnswer ? 'True' : 'False',
      answer:
        booleanAnswer === null ? question.answer : booleanAnswer ? 'True' : 'False',
    };
  }

  // Short-answer geography files commonly use:
  // answer: 'Pacific Ocean'
  if (type === 'short-answer' || type === 'short' || type === 'text') {
    const correctAnswer =
      question.correctAnswer ||
      question.answer ||
      question.expected_answer ||
      question.modelAnswer ||
      '';

    return {
      ...question,
      id,
      type: 'short-answer',
      correctAnswer,
      answer: correctAnswer,
      acceptedAnswers: question.acceptedAnswers || question.accepted_answers || [],
    };
  }

  return {
    ...question,
    id,
  };
}

export function getNormalisedGeographyPracticeBank(topic, tier = 'warmUp') {
  return getGeographyPracticeBank(topic, tier).map((question, index) =>
    normaliseQuestion(question, index, topic?.id || topic?.slug || 'geography-topic')
  );
}

function normaliseTopic(topic, sectionMeta) {
  if (!topic || typeof topic !== 'object') return topic;

  const normalisedPractice = normalisePracticeToTieredBank(topic.practice);

  return {
    ...topic,
    sectionId: sectionMeta.id,
    sectionSlug: sectionMeta.slug,
    sectionTitle: sectionMeta.title,
    aqaPaper: topic.aqaPaper || sectionMeta.aqaPaper,
    practiceBank: normalisedPractice,
    questionCount:
      getGeographyPracticeCount(topic) +
      (Array.isArray(topic.questions) ? topic.questions.length : 0) +
      (Array.isArray(topic.quiz) ? topic.quiz.length : 0),
  };
}

function buildSection(module, meta, index) {
  const sectionNumber = index + 1;
  const possibleSection = getPossibleSection(module, sectionNumber);

  if (
    possibleSection &&
    typeof possibleSection === 'object' &&
    !Array.isArray(possibleSection) &&
    Array.isArray(possibleSection.topics)
  ) {
    const topics = possibleSection.topics.map((topic) => normaliseTopic(topic, meta));

    return {
      ...meta,
      ...possibleSection,
      ...meta,
      topics,
      status: possibleSection.status || 'live',
      topicCount: topics.length,
    };
  }

  const topics = extractTopics(module, sectionNumber).map((topic) =>
    normaliseTopic(topic, meta)
  );

  return {
    ...meta,
    topics,
    status: 'live',
    topicCount: topics.length,
  };
}

export const geographySections = sectionModules.map((module, index) =>
  buildSection(module, SECTION_META[index], index)
);

export const getTotalGeographyTopics = () =>
  geographySections.reduce((acc, section) => acc + (section.topics?.length || 0), 0);

export const getTotalGeographyQuestions = () =>
  geographySections.reduce((sectionTotal, section) => {
    const topicQuestionCount = (section.topics || []).reduce((topicTotal, topic) => {
      return topicTotal + (topic.questionCount || 0);
    }, 0);

    return sectionTotal + topicQuestionCount;
  }, 0);

export const findGeographyTopic = (sectionSlug, topicSlug) => {
  const section = geographySections.find((s) => s.slug === sectionSlug);

  if (!section) {
    return { section: null, topic: null };
  }

  const topic = (section.topics || []).find((t) => t.slug === topicSlug);

  return {
    section,
    topic: topic || null,
  };
};

export const getGeographySectionBySlug = (slug) =>
  geographySections.find((section) => section.slug === slug) || null;

export const getGeographyTopicBySlug = (topicSlug) => {
  for (const section of geographySections) {
    const topic = (section.topics || []).find((t) => t.slug === topicSlug);

    if (topic) {
      return { section, topic };
    }
  }

  return { section: null, topic: null };
};

export const getGeographySectionsByPaper = (paper) =>
  geographySections.filter((section) => section.aqaPaper === paper);

export const getLiveGeographySections = () =>
  geographySections.filter((section) => section.status !== 'draft');

export const getGeographySectionSummary = () =>
  geographySections.map((section) => ({
    id: section.id,
    slug: section.slug,
    title: section.title,
    description: section.description,
    aqaPaper: section.aqaPaper,
    topicCount: section.topics?.length || 0,
    questionCount: (section.topics || []).reduce(
      (total, topic) => total + (topic.questionCount || 0),
      0
    ),
    status: section.status || 'live',
  }));

export default geographySections;